import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
//import './CustomerReturnsList.css';
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
    UncontrolledDropdown
} from 'reactstrap';
import {
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaAngleLeft,
    FaAngleRight,
    FaBoxOpen,
    FaCalendarAlt,
    FaCheckCircle,
    FaClock,
    FaEdit,
    FaFileExport,
    FaFileInvoice,
    FaFilePdf,
    FaFilter,
    FaInfoCircle,
    FaMoneyBillWave,
    FaPlus,
    FaSearch,
    FaTimes,
    FaTrash,
    FaUndo
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { customerReturnActions } from '../../sagas/customerReturnSlice';
import { formatPrice } from '../../helpers/utility';
import CustomerReturnForm from './CustomerReturnForm';
import "jspdf-autotable";

const CustomerReturnsList = () => {
    const dispatch = useDispatch();
    const { customerReturns, loading } = useSelector((state) => state.customerReturns);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();

    const [selectedReturn, setSelectedReturn] = useState(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const searchInputRef = useRef(null);

    // Filter states
    const [filters, setFilters] = useState({
        clientId: '',
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: ''
    });

    const navigate = useNavigate();



    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [customerReturns, filters]);

    useEffect(() => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }, [filters]);


    const debouncedSearch = useCallback(
        debounce((query) => {
            dispatch(customerReturnActions.list({
                ...filters,
                query: query
            }));
        }, 1500),
        [filters.query]
    );

    const clearFilters = () => {
        setFilters({
            query: '',
            clientId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
    };


    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleEdit = (returnItem) => {
        setSelectedReturn(returnItem);
        setShowModal(true);
    }

    const handleCloseModal = () => {
        setShowModal(false);
        if (selectedReturn) {
            setSelectedReturn(null);
        }

        const payload = {
            ...filters
        };
        dispatch(customerReturnActions.list(payload));
    };

    const handleValidateReturn = (returnItem) => {
        Swal.fire({
            title: t('customerReturns.confirmValidate'),
            text: t('customerReturns.confirmValidateMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.validatedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.validate(payload));
        });
    }

    const handleDeleteReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.deleted'),
                        text: t('customerReturn.deletedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.delete(payload));
        });
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUndo className="text-primary me-2" />
                                {t('customerReturns.list')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="me-2" /> {t('customerReturns.new')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col lg={7} md={4} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('reglement.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            query: value
                                                        }));
                                                        debouncedSearch(value);
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch(e.target.value.toUpperCase());
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover table-striped table-bordered">
                                    <thead className="thead-light">
                                        <tr>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.numero')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.date')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('delivery.commercial')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('delivery.vendeur')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.client')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.refundMode')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.montantTTC')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.montantHT')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.status')}
                                            </th>
                                            <th className="align-middle text-center">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customerReturns.map((returnItem) => (
                                            <tr key={returnItem.id}>
                                                <td className="align-middle text-center">
                                                    {returnItem.numeroRetour}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {new Date(returnItem.dateOperation).toLocaleDateString('fr-FR')}
                                                </td>

                                                <td className="align-middle text-center">
                                                    {returnItem.commercial ? returnItem.commercial?.fullName || 'N/A' : 'N/A'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.createdBy || 'N/A'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.client ? returnItem.client?.entreprise || returnItem.client?.prenom + ' ' + returnItem.client?.nom || 'N/A' : 'N/A'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.modeRemboursement === 'AVOIR' && (
                                                        <span className="badge bg-primary">{t('customerReturns.avoir')}</span>
                                                    )}
                                                    {returnItem.modeRemboursement === 'REMBOURSEMENT' && (
                                                        <span className="badge bg-info">{t('customerReturns.remboursement')}</span>
                                                    )}
                                                    {returnItem.modeRemboursement === 'ECHANGE' && (
                                                        <span className="badge bg-dark">{t('customerReturns.echange')}</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">{formatPrice(returnItem.montantTTC, false)}</td>
                                                <td className="align-middle text-center">{formatPrice(returnItem.montantHT, false)}</td>
                                                <td className="align-middle text-center">
                                                    {returnItem.statut === 'BROUILLON' && (
                                                        <span className="badge bg-warning">{t('customerReturns.brouillon')}</span>
                                                    )}
                                                    {returnItem.statut === 'ANNULE' && (
                                                        <span className="badge bg-danger">{t('customerReturns.annule')}</span>
                                                    )}
                                                    {returnItem.statut === 'VALIDE' && (
                                                        <span className="badge bg-success">{t('customerReturns.valide')}</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div className="btn-group">
                                                        <Button
                                                            color="secondary"
                                                            title={t('customerReturns.generatePdf')}
                                                            onClick={() => console.log('Generate PDF')}
                                                            className="me-1"
                                                            size="sm"
                                                        >
                                                            <FaFilePdf />
                                                        </Button>
                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="success"
                                                                title={t('customerReturns.validate')}
                                                                onClick={() => handleValidateReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaCheckCircle />
                                                            </Button>
                                                        )}

                                                        <Button
                                                            color="warning"
                                                            title={t('customerReturns.edit')}
                                                            onClick={() => handleEdit(returnItem)}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaEdit />
                                                        </Button>

                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="danger"
                                                                title={t('customerReturns.delete')}
                                                                onClick={() => handleDeleteReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        )}

                                                        {returnItem.statut === 'COMPTABILISE' &&
                                                            returnItem.modeRemboursement === 'REMBOURSEMENT' &&
                                                            !returnItem.estRembourse && (
                                                                <Button
                                                                    color="info"
                                                                    title={t('customerReturns.processRefund')}
                                                                    onClick={() => navigate(`/ventes/remboursements/new?retourId=${returnItem.id}`)}
                                                                    className="me-1"
                                                                    size="sm"
                                                                >
                                                                    <FaMoneyBillWave />
                                                                </Button>
                                                            )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {
                                            customerReturns.length === 0 && (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {
                                                                t('customerReturns.noReturns')
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="6" className="text-end">{t('common.totals')}</td>
                                            <td className="text-center">{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantTTC || 0), 0), false)}</td>
                                            <td className="text-center">{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantHT || 0), 0), false)}</td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                        </>
                    )}
                </CardBody>
            </Card>

            {/* Modal for Customer Return Form */}
            <Modal isOpen={showModal} toggle={handleCloseModal} size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseModal}>
                    {selectedReturn ? `${t('customerReturns.edit')} N° ${selectedReturn.numeroRetour}` : t('customerReturns.new')}
                </ModalHeader>
                <ModalBody>
                    <CustomerReturnForm customerReturn={selectedReturn} onClose={handleCloseModal} />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default CustomerReturnsList;

